
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { getCategories, createPrompt } from "@/services/promptService";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { AlertCircle, Globe, Info, Trophy } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import type { Category } from "@/services/promptService";

interface EnhancedCategory extends Category {
  description?: string;
  icon?: string;
  parent_id?: string | null;
  parent?: {
    id: string;
    name: string;
    slug: string;
  };
  children?: EnhancedCategory[];
}

const PromptForm = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [source, setSource] = useState("");
  const [categories, setCategories] = useState<EnhancedCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const data = await getCategories();
        
        // Group into parent-child hierarchy for the select component
        const parentCategories: EnhancedCategory[] = [];
        const childCategories: { [key: string]: EnhancedCategory[] } = {};
        
        // First pass: identify parents and children
        data.forEach(cat => {
          const enhancedCat = cat as EnhancedCategory;
          if (enhancedCat.parent_id) {
            if (!childCategories[enhancedCat.parent_id]) {
              childCategories[enhancedCat.parent_id] = [];
            }
            childCategories[enhancedCat.parent_id].push(enhancedCat);
          } else {
            parentCategories.push(enhancedCat);
          }
        });
        
        // Second pass: assign children to parents
        parentCategories.forEach(parent => {
          parent.children = childCategories[parent.id] || [];
        });
        
        setCategories(parentCategories);
      } catch (error) {
        console.error("Error loading categories:", error);
        // Fallback to flat list
        const data = await getCategories();
        setCategories(data as EnhancedCategory[]);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    
    loadCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create a prompt.",
        variant: "destructive",
      });
      navigate('/sign-in');
      return;
    }
    
    if (!title || !description || !content || !categoryId) {
      toast({
        title: "Missing fields",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const promptId = await createPrompt({
        title,
        description,
        content,
        category_id: categoryId,
        user_id: user.id,
        source: source || null
      });
      
      if (!promptId) {
        throw new Error("Failed to create prompt");
      }
      
      // Show a success toast with gamification info
      toast({
        title: "Prompt submitted successfully!",
        description: "Your prompt has been submitted for review. You've earned 5 points!",
      });
      
      // Redirect to dashboard
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error creating prompt:", error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Find a category with the given ID across the full hierarchy
  const findCategoryById = (id: string, categories: EnhancedCategory[]): EnhancedCategory | null => {
    for (const category of categories) {
      if (category.id === id) return category;
      if (category.children) {
        const found = findCategoryById(id, category.children);
        if (found) return found;
      }
    }
    return null;
  };

  // Get the selected category details
  const selectedCategory = categoryId ? findCategoryById(categoryId, categories) : null;

  return (
    <>
      {!user && (
        <Alert className="mb-6" variant="default">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Sign in required</AlertTitle>
          <AlertDescription>
            You need to <Link to="/sign-in" className="font-medium underline">sign in</Link> to submit a prompt. Create an account to contribute to the community.
          </AlertDescription>
        </Alert>
      )}
      
      <Card className="mb-6 border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Earn Points by Contributing
          </CardTitle>
          <CardDescription>
            Every approved prompt submission helps you advance in our contributor leaderboard.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="text-green-600 font-medium">+5</span>
              </div>
              <span>Submit a prompt for review</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="text-green-600 font-medium">+10</span>
              </div>
              <span>Get your prompt approved</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="text-green-600 font-medium">+2</span>
              </div>
              <span>Each like your prompts receive</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="text-green-600 font-medium">+1</span>
              </div>
              <span>Each time someone uses your prompt</span>
            </div>
            <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3" />
              <span>Check your progress on the <Link to="/leaderboard" className="font-medium underline">leaderboard</Link></span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
        <div className="space-y-2">
          <Label htmlFor="title">Title <span className="text-destructive">*</span></Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a descriptive title for your prompt"
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="description">Description <span className="text-destructive">*</span></Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Explain what your prompt does and how it can be used"
            required
            className="min-h-[80px]"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="content">Prompt Content <span className="text-destructive">*</span></Label>
          <Textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter your prompt content here..."
            required
            className="min-h-[200px] font-mono"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="category">Category <span className="text-destructive">*</span></Label>
          {isLoadingCategories ? (
            <div className="py-2 px-4 border rounded-md h-10 flex items-center gap-2">
              <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
              <span className="text-muted-foreground">Loading categories...</span>
            </div>
          ) : (
            <div className="space-y-2">
              <Select value={categoryId} onValueChange={setCategoryId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((parentCategory) => (
                    <SelectGroup key={parentCategory.id}>
                      <SelectItem value={parentCategory.id}>{parentCategory.name}</SelectItem>
                      {parentCategory.children && parentCategory.children.length > 0 && (
                        parentCategory.children.map((childCategory) => (
                          <SelectItem 
                            key={childCategory.id} 
                            value={childCategory.id}
                            className="pl-6"
                          >
                            ↳ {childCategory.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedCategory?.description && (
                <div className="text-sm text-muted-foreground p-3 bg-secondary/30 rounded border">
                  <p className="font-medium text-foreground mb-1">About this category:</p>
                  <p>{selectedCategory.description}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="source" className="flex items-center gap-2">
            <span>Source</span>
            <span className="text-xs text-muted-foreground font-normal">(optional)</span>
          </Label>
          <div className="relative">
            <Globe className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
            <Input
              id="source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. reddit.com/r/chatgpt, twitter.com/username"
              className="pl-10"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Add attribution if this prompt is from another source like Reddit, X/Twitter, or other platforms
          </p>
        </div>
        
        <div className="flex gap-4 pt-4">
          <Button 
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              user ? "Submit Prompt" : "Sign in to Submit"
            )}
          </Button>
        </div>
      </form>
    </>
  );
};

export default PromptForm;
